import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Skeleton,
    IconButton,
    Tooltip,
    Divider,
    FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    LocalOffer as CouponIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Image as ImageIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { createCouponInitial, updateCouponInitial, deleteCouponInitial, getCouponInitials, MAIN_URL } from "../../../api/api";
import AdminHeader from "../../../components/AdminHeader/AdminHeader";
import Swal from 'sweetalert2';
import { getCouponImageStyles } from "../../../constants/imageSizes";
import { formatAdminDate } from "../../../utils/formatDate";
import { getAdminNavLinks } from "../../../theme/adminNavLinks";
import { pageBackgroundSx } from "../../../theme/theme";
import { ADMIN_PAGE_GUIDES } from "../../../constants/adminPageGuides";

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
        },
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '6px 16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    },
}));

const ImagePreview = styled(Box)(({ theme }) => ({
    width: 250,
    height: 90,
    flexShrink: 0,
    borderRadius: 6,
    border: '1px dashed #cfcfcf',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
}));

const fieldHint = (hint, error) => error || hint;

const emptyForm = {
    name: "",
    description: "",
    coupon_image: null,
    offer_image: null,
};

const getImageFallbackSources = (imageName) => {
    if (!imageName) return [];

    const fallbackSources = [`${MAIN_URL}/images/${imageName}`];
    const webpToJpgName = imageName.replace(/\.webp$/i, '.jpg');

    if (webpToJpgName !== imageName) {
        fallbackSources.push(`${MAIN_URL}/images/${webpToJpgName}`);
    }

    fallbackSources.push(`/images/${imageName}`);
    if (webpToJpgName !== imageName) {
        fallbackSources.push(`/images/${webpToJpgName}`);
    }

    return [...new Set(fallbackSources)];
};

const CreateCoupon = () => {
    const formCardRef = useRef(null);
    const [form, setForm] = useState(emptyForm);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [couponImagePreview, setCouponImagePreview] = useState(null);
    const [offerImagePreview, setOfferImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [couponInitials, setCouponInitials] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCouponInitials();
    }, []);

    const fetchCouponInitials = async () => {
        setLoadingCoupons(true);
        setError(null);
        try {
            const response = await getCouponInitials();
            setCouponInitials(response.data.data || []);
        } catch (err) {
            console.error("Error loading coupons:", err);
            setError(err.response?.data?.error || "Failed to load coupon. Please try again.");
        } finally {
            setLoadingCoupons(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    const convertImageToWebp = (file, quality = 0.85) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const context = canvas.getContext('2d');

                if (!context) {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error('Failed to process image'));
                    return;
                }

                context.drawImage(img, 0, 0);
                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(objectUrl);
                        if (!blob) {
                            reject(new Error('Failed to convert image to WebP'));
                            return;
                        }

                        const fileName = file.name.replace(/\.[^/.]+$/, '') || 'coupon-image';
                        resolve(
                            new File([blob], `${fileName}.webp`, {
                                type: 'image/webp',
                                lastModified: Date.now(),
                            })
                        );
                    },
                    'image/webp',
                    quality
                );
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Failed to load selected image'));
            };

            img.src = objectUrl;
        });

    const handleImageChange = async (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setErrors((prev) => ({
                    ...prev,
                    [type]: 'Please select a valid image file',
                }));
                return;
            }
            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setErrors((prev) => ({
                    ...prev,
                    [type]: 'Image size should be less than 2MB',
                }));
                return;
            }

            try {
                const webpFile = await convertImageToWebp(file);

                // Validate converted WebP size (max 2MB)
                if (webpFile.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({
                        ...prev,
                        [type]: 'Converted WebP image is still above 2MB. Please use a smaller image.',
                    }));
                    return;
                }

                setForm((prev) => ({
                    ...prev,
                    [type]: webpFile,
                }));

                // Create preview
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (type === 'coupon_image') {
                        setCouponImagePreview(reader.result);
                    } else {
                        setOfferImagePreview(reader.result);
                    }
                };
                reader.readAsDataURL(webpFile);
            } catch (conversionError) {
                console.error('Image conversion error:', conversionError);
                setErrors((prev) => ({
                    ...prev,
                    [type]: 'Could not convert image to WebP. Please try another image.',
                }));
                return;
            }

            // Clear error
            if (errors[type]) {
                setErrors((prev) => ({
                    ...prev,
                    [type]: null,
                }));
            }
        }
    };

    const resetForm = () => {
        setForm(emptyForm);
        setEditingCoupon(null);
        setCouponImagePreview(null);
        setOfferImagePreview(null);
        setErrors({});
    };

    const handleEditCoupon = (coupon) => {
        setEditingCoupon(coupon);
        setForm({
            name: coupon.name || "",
            description: coupon.description || "",
            coupon_image: null,
            offer_image: null,
        });
        setCouponImagePreview(
            coupon.coupon_image_url
                ? getImageFallbackSources(coupon.coupon_image_url)[0]
                : null
        );
        setOfferImagePreview(
            coupon.offer_image_url
                ? getImageFallbackSources(coupon.offer_image_url)[0]
                : null
        );
        setErrors({});
        formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleCancelEdit = () => {
        resetForm();
    };

    const showCantDeleteAlert = (reason) => {
        Swal.fire({
            icon: 'warning',
            title: "Can't delete this coupon",
            text: reason || 'This coupon is in use and cannot be deleted.',
            confirmButtonText: 'OK',
        });
    };

    const getDeleteBlockReason = (coupon) => {
        if (coupon.delete_block_reason) {
            return coupon.delete_block_reason;
        }
        if (coupon.can_delete === false) {
            return 'This coupon is in use and cannot be deleted.';
        }
        return null;
    };

    const handleDeleteCoupon = async (coupon) => {
        const blockReason = getDeleteBlockReason(coupon);
        if (blockReason) {
            showCantDeleteAlert(blockReason);
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Delete coupon template "${coupon.name}"? This cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it',
        });

        if (!result.isConfirmed) {
            return;
        }

        setDeletingId(coupon.id);
        try {
            await deleteCouponInitial(coupon.id);
            if (editingCoupon?.id === coupon.id) {
                resetForm();
            }
            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Coupon template deleted successfully.',
                timer: 2000,
                showConfirmButton: false,
            });
            fetchCouponInitials();
        } catch (err) {
            console.error('Error deleting coupon:', err);
            const status = err.response?.status;
            const apiError = err.response?.data?.error || err.response?.data?.message;

            if (status === 409 || (apiError && apiError.toLowerCase().includes("can't delete"))) {
                const reason = apiError?.replace(/^Can't delete this coupon\.?\s*/i, '') || blockReason;
                showCantDeleteAlert(reason || 'This coupon is in use and cannot be deleted.');
                return;
            }

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: apiError || 'Failed to delete coupon. Please try again.',
                confirmButtonText: 'OK',
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!form.name.trim()) {
            newErrors.name = "Coupon name is required";
        }
        if (!editingCoupon && !form.coupon_image) {
            newErrors.coupon_image = "Coupon image is required";
        }
        if (editingCoupon && !form.coupon_image && !editingCoupon.coupon_image_url) {
            newErrors.coupon_image = "Coupon image is required";
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            if (editingCoupon) {
                await updateCouponInitial(editingCoupon.id, form);
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Coupon updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                await createCouponInitial(form);
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Coupon created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            resetForm();
            fetchCouponInitials();
        } catch (err) {
            console.error(editingCoupon ? "Error updating coupon:" : "Error creating coupon:", err);
            const errorMessage = err.response?.data?.error || err.response?.data?.message
                || (editingCoupon ? "Failed to update coupon. Please try again." : "Failed to create coupon. Please try again.");
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchCouponInitials();
    };

    const handleCouponImageError = (event, imageName) => {
        const img = event.currentTarget;
        const fallbackSources = getImageFallbackSources(imageName);
        const currentIndex = Number(img.dataset.fallbackIndex || 0);
        const nextIndex = currentIndex + 1;

        if (fallbackSources[nextIndex]) {
            img.dataset.fallbackIndex = String(nextIndex);
            img.src = fallbackSources[nextIndex];
            return;
        }

        img.style.display = 'none';
    };

    return (
            <Box sx={pageBackgroundSx}>
                <Box sx={{ maxWidth: '100%', mx: 'auto', p: { xs: 1, sm: 1.5 } }}>
                    <AdminHeader 
                        title="Create Coupon"
                        icon={CouponIcon}
                        navigationLinks={getAdminNavLinks("/create_coupon")}
                        guide={ADMIN_PAGE_GUIDES.createCoupon}
                    />

                    {/* Create / Edit Coupon Form */}
                    <StyledCard ref={formCardRef} sx={{ mb: 1.5 }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Typography variant="subtitle1" sx={{
                                mb: 0.5,
                                fontWeight: 700,
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                <CouponIcon sx={{ mr: 0.75, fontSize: 20 }} />
                                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                            </Typography>
                            {editingCoupon && (
                                <Alert severity="warning" sx={{ mt: 1, mb: 1, py: 0.5 }}>
                                    Editing <strong>{editingCoupon.name}</strong>. Leave images unchanged to keep the current files.
                                </Alert>
                            )}
                            <Divider sx={{ mb: 1.5 }} />
                            
                            <form onSubmit={handleSubmit}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                    alignItems: { md: 'flex-start' },
                                }}>
                                    <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 6px)', md: '12%' }, flexShrink: 0 }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Coupon Name *"
                                            name="name"
                                            size="small"
                                            value={form.name}
                                            onChange={handleChange}
                                            error={!!errors.name}
                                            helperText={fieldHint('Name shown to users.', errors.name)}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box sx={{
                                        width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
                                        flex: { md: '1.5 1 0' },
                                        minWidth: { md: 180 },
                                    }}>
                                        <StyledTextField
                                            fullWidth
                                            label="Description"
                                            name="description"
                                            size="small"
                                            value={form.description}
                                            onChange={handleChange}
                                            helperText="Optional offer text."
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box sx={{
                                        width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
                                        flex: { md: '2.2 1 0' },
                                        minWidth: { md: 340 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <ImagePreview>
                                                {couponImagePreview ? (
                                                    <img src={couponImagePreview} alt="Coupon preview" loading="lazy" decoding="async" />
                                                ) : (
                                                    <ImageIcon fontSize="small" color="disabled" />
                                                )}
                                            </ImagePreview>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <input
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    id="coupon-image-upload"
                                                    type="file"
                                                    onChange={(e) => handleImageChange(e, 'coupon_image')}
                                                />
                                                <label htmlFor="coupon-image-upload">
                                                    <Button
                                                        variant="outlined"
                                                        component="span"
                                                        size="small"
                                                        startIcon={<ImageIcon />}
                                                    >
                                                        Coupon Image {editingCoupon ? '' : '*'}
                                                    </Button>
                                                </label>
                                                <FormHelperText sx={{ mt: 0.5, mx: 0, lineHeight: 1.3 }}>
                                                    {errors.coupon_image || 'Main image. Max 2 MB.'}
                                                </FormHelperText>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
                                        flex: { md: '1.9 1 0' },
                                        minWidth: { md: 300 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <ImagePreview>
                                                {offerImagePreview ? (
                                                    <img src={offerImagePreview} alt="Offer preview" loading="lazy" decoding="async" />
                                                ) : (
                                                    <ImageIcon fontSize="small" color="disabled" />
                                                )}
                                            </ImagePreview>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <input
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    id="offer-image-upload"
                                                    type="file"
                                                    onChange={(e) => handleImageChange(e, 'offer_image')}
                                                />
                                                <label htmlFor="offer-image-upload">
                                                    <Button
                                                        variant="outlined"
                                                        component="span"
                                                        size="small"
                                                        startIcon={<ImageIcon />}
                                                    >
                                                        Offer Image
                                                    </Button>
                                                </label>
                                                <FormHelperText sx={{ mt: 0.5, mx: 0, lineHeight: 1.3 }}>
                                                    Optional extra image.
                                                </FormHelperText>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    mt: 1.5,
                                    pt: 1,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                    gap: 0.75,
                                }}>
                                    {editingCoupon && (
                                        <StyledButton
                                            type="button"
                                            variant="outlined"
                                            size="small"
                                            startIcon={<CloseIcon />}
                                            disabled={loading}
                                            onClick={handleCancelEdit}
                                            sx={{ py: 0.5, px: 2, minHeight: 30, fontSize: '0.8125rem', width: 'auto' }}
                                        >
                                            Cancel
                                        </StyledButton>
                                    )}
                                    <StyledButton
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : (editingCoupon ? <EditIcon /> : <AddIcon />)}
                                        disabled={loading}
                                        sx={{ py: 0.5, px: 2, minHeight: 30, fontSize: '0.8125rem', width: 'auto' }}
                                    >
                                        {loading
                                            ? (editingCoupon ? 'Updating...' : 'Creating...')
                                            : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                                    </StyledButton>
                                </Box>
                            </form>
                        </CardContent>
                    </StyledCard>

                    {/* Existing Coupons Table */}
                    <StyledCard>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: "divider" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Existing Coupons
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Coupon templates available for campaigns. Edit loads the form above.
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Refresh">
                                        <IconButton onClick={handleRefresh} size="small">
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ m: 1.5, py: 0.5 }}>
                                    {error}
                                </Alert>
                            )}

                            {loadingCoupons ? (
                                <Box sx={{ p: 1.5 }}>
                                    {[...Array(5)].map((_, index) => (
                                        <Skeleton key={index} variant="rectangular" height={44} sx={{ mb: 0.5, borderRadius: 1 }} />
                                    ))}
                                </Box>
                            ) : couponInitials.length === 0 ? (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <CouponIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                                    <Typography variant="body1" color="text.secondary" gutterBottom>
                                        No Coupons Found
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Create your first coupon using the form above
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%', maxHeight: { xs: 360, sm: 480 } }}>
                                    <Table size="small" sx={{ minWidth: 600 }}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: "primary.main" }}>
                                                <TableCell sx={{ fontWeight: 700, py: 0.75, color: 'white', fontSize: '0.8rem' }}>Image</TableCell>
                                                <TableCell sx={{ fontWeight: 700, py: 0.75, color: 'white', fontSize: '0.8rem' }}>Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, py: 0.75, color: 'white', fontSize: '0.8rem' }}>Description</TableCell>
                                                <TableCell sx={{ fontWeight: 700, py: 0.75, color: 'white', fontSize: '0.8rem' }}>Created</TableCell>
                                                <TableCell sx={{ fontWeight: 700, py: 0.75, color: 'white', fontSize: '0.8rem' }} align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {couponInitials.map((coupon) => (
                                                <TableRow
                                                    key={coupon.id}
                                                    hover
                                                    selected={editingCoupon?.id === coupon.id}
                                                    sx={editingCoupon?.id === coupon.id ? { bgcolor: 'action.selected' } : undefined}
                                                >
                                                    <TableCell sx={{ py: 0.5 }}>
                                                        {coupon.coupon_image_url ? (
                                                            <Box
                                                                component="img"
                                                                src={getImageFallbackSources(coupon.coupon_image_url)[0]}
                                                                onError={(event) => handleCouponImageError(event, coupon.coupon_image_url)}
                                                                data-fallback-index="0"
                                                                alt={coupon.name}
                                                                loading="lazy"
                                                                decoding="async"
                                                                sx={getCouponImageStyles('SMALL')}
                                                            />
                                                        ) : (
                                                            <Box sx={{ width: 50, height: 30, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <ImageIcon color="disabled" />
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5 }}>
                                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                                                            {coupon.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, maxWidth: 200 }}>
                                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem' }}>
                                                            {coupon.description || "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                            {formatAdminDate(coupon.created_at)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5 }} align="center">
                                                        <Stack direction="row" spacing={0.5} justifyContent="center">
                                                            <Tooltip title="Edit coupon">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleEditCoupon(coupon)}
                                                                    disabled={loading || deletingId === coupon.id}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    getDeleteBlockReason(coupon)
                                                                        ? `Can't delete: ${getDeleteBlockReason(coupon)}`
                                                                        : 'Delete coupon'
                                                                }
                                                            >
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDeleteCoupon(coupon)}
                                                                        disabled={loading || deletingId === coupon.id}
                                                                    >
                                                                    {deletingId === coupon.id ? (
                                                                        <CircularProgress size={18} color="inherit" />
                                                                    ) : (
                                                                        <DeleteIcon fontSize="small" />
                                                                    )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </StyledCard>
                </Box>
            </Box>
    );
};

export default CreateCoupon;

